import { Box, Chip, Link as MuiLink, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Link } from 'react-router-dom';

import {
  formatCanvassingSessionDuration,
  formatCanvassingSessionTimestamp,
  formatReceivedAndExpected,
} from './canvassingSessionFormatters';
import {
  getCanvassingSessionCaptureStatusLabel,
  getCanvassingSessionProcessingStatusLabel,
} from './canvassingSessionLabels';
import type { BrokerageCanvasserDirectoryStatus } from '../BrokerageCanvasserDirectoryContext';
import { CanvassingSessionTableFooter } from './CanvassingSessionTableFooter';
import type { CanvassingSessionListItem } from './useCanvassingSessionList';

export type { CanvassingSessionListItem };

export type CanvassingSessionTableProps = {
  items: CanvassingSessionListItem[];
  total: number;
  page: number;
  rowsPerPage: number;
  returnTo: string;
  canvasserDirectoryStatus: BrokerageCanvasserDirectoryStatus;
  resolveCanvasserLabel: (canvasserId: string) => string;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

export const CanvassingSessionTable = ({
  items,
  total,
  page,
  rowsPerPage,
  returnTo,
  canvasserDirectoryStatus,
  resolveCanvasserLabel,
  onPageChange,
  onRowsPerPageChange,
}: CanvassingSessionTableProps) => (
  <Box data-testid="canvassing-session-table">
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1320 }}>
        <TableHead>
          <TableRow>
            <TableCell>Session ID</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Canvasser</TableCell>
            <TableCell>Capture status</TableCell>
            <TableCell>Processing status</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Audio received / expected</TableCell>
            <TableCell>GPS received / reported</TableCell>
            <TableCell>Session door visits</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id} hover>
              <TableCell>
                <MuiLink component={Link} to={`/canvassing-sessions/${item.id}`} state={{ returnTo }}>
                  {item.id}
                </MuiLink>
              </TableCell>
              <TableCell>{formatCanvassingSessionTimestamp(item.startedAt)}</TableCell>
              <TableCell aria-label={canvasserDirectoryStatus === 'loading' ? 'Loading canvasser name' : undefined}>
                {resolveCanvasserLabel(item.canvasserId)}
              </TableCell>
              <TableCell>
                <Chip label={getCanvassingSessionCaptureStatusLabel(item.captureStatus)} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip label={getCanvassingSessionProcessingStatusLabel(item.processingStatus)} size="small" variant="outlined" />
              </TableCell>
              <TableCell>{formatCanvassingSessionDuration(item.durationSeconds)}</TableCell>
              <TableCell>{formatReceivedAndExpected(item.receivedAudioSegments, item.expectedAudioSegments)}</TableCell>
              <TableCell>{formatReceivedAndExpected(item.receivedGpsPoints, item.reportedGpsPoints)}</TableCell>
              <TableCell>{item.doorVisitCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <CanvassingSessionTableFooter
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  </Box>
);
